import type { Point } from '../types/document'
import type { EdgeMap } from './edges'
import { simplify } from './edges'

/**
 * Draw it rough, get it straight.
 *
 * Nobody draws a straight line with a mouse, and nobody comes close with a
 * finger on a tablet held in one hand in somebody's driveway. Every hand-drawn
 * roofline in this app so far has been a little bent, a little short, and a
 * little off the actual edge of the roof, and at night with lights on it that
 * reads immediately as fake.
 *
 * A house, though, is made of straight lines, and they are already in the
 * photo. So rather than asking the contractor to be accurate, take the shape
 * they meant - which their sloppy stroke describes perfectly well - and lock it
 * onto the architecture underneath.
 *
 * Three steps:
 *   1. Reduce the stroke to its corners (RDP). A drag round a garage door is
 *      three straight pieces and two corners, whatever the hand did.
 *   2. Slide and tilt each piece onto the strongest straight edge near it.
 *   3. Intersect neighboring pieces so the corners actually meet, because
 *      after step 2 each piece has moved independently and they no longer do.
 *
 * If step 2 finds nothing convincing it leaves the piece exactly where it was
 * drawn. A tool that quietly invents a roof edge is worse than one that leaves
 * your line alone.
 */

/** Bilinear, so a line that sits between two pixel rows still scores honestly. */
function magAt(map: EdgeMap, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= map.w - 1 || y >= map.h - 1) return 0
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const fx = x - x0
  const fy = y - y0
  const i = y0 * map.w + x0
  const a = map.mag[i]
  const b = map.mag[i + 1]
  const c = map.mag[i + map.w]
  const d = map.mag[i + map.w + 1]
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy
}

/** Mean edge strength along a line, in edge-map pixels. */
function lineScore(map: EdgeMap, ax: number, ay: number, bx: number, by: number): number {
  const len = Math.hypot(bx - ax, by - ay)
  const n = Math.max(8, Math.min(240, Math.round(len)))
  let sum = 0
  for (let i = 0; i <= n; i++) {
    const t = i / n
    sum += magAt(map, ax + (bx - ax) * t, ay + (by - ay) * t)
  }
  return sum / (n + 1)
}

/**
 * Slide and tilt one straight piece onto the real edge beneath it.
 *
 * Both moves are perpendicular to the line the user drew: sliding shifts both
 * ends the same way, tilting shifts them opposite ways. Between them they reach
 * any nearby line without ever letting the piece rotate away from what was
 * meant or slide along its own length and change where it starts and stops.
 */
export function straightenSegment(
  map: EdgeMap,
  a: Point,
  b: Point,
  /*
   * How far the line may slide looking for a better edge.
   *
   * This was 0.02 of the photo width - 48 pixels on a 2400px shot - against a
   * penalty for moving that topped out at 0.06. On a barrel tile roof there is
   * a stronger line a course or two below whatever you drew, so the run
   * consistently slid downhill: measured twelve screen pixels low at
   * fit-to-window, while the same run drawn on a wall, where there is nothing
   * to slide to, landed dead on.
   *
   * Reported as: the line of lights comes out lower than where I put the dot.
   *
   * Auto-correcting onto the roof edge is the feature and it stays. It just
   * has to be a correction rather than an opinion, so the reach is under half
   * what it was and moving now costs four times as much. A hand that missed by
   * a few pixels still gets rescued; a hand that meant it is left alone.
   */
  searchFrac = 0.008,
): { a: Point; b: Point; score: number } {
  const ax = a.x * map.w
  const ay = a.y * map.h
  const bx = b.x * map.w
  const by = b.y * map.h
  const dx = bx - ax
  const dy = by - ay
  const len = Math.hypot(dx, dy)
  if (len < 4) return { a, b, score: 0 }

  // Unit normal to the drawn direction.
  const nx = -dy / len
  const ny = dx / len

  const r = Math.max(4, Math.round(searchFrac * map.w))
  const step = Math.max(1, Math.round(r / 12))

  const drawn = lineScore(map, ax, ay, bx, by)
  let best = drawn
  let bo = 0
  let bt = 0

  for (let o = -r; o <= r; o += step) {
    // Tilt is deliberately a smaller search than slide. The angle a contractor
    // draws is usually close to right - they can see the roof - while the
    // offset is whatever their thumb did.
    const tr = Math.round(r * 0.6)
    for (let t = -tr; t <= tr; t += step) {
      const a2x = ax + nx * (o - t)
      const a2y = ay + ny * (o - t)
      const b2x = bx + nx * (o + t)
      const b2y = by + ny * (o + t)
      const s = lineScore(map, a2x, a2y, b2x, b2y)
      // Stay near what was drawn unless the edge is clearly better.
      const penalty = ((Math.abs(o) + Math.abs(t)) / r) * 0.25
      if (s - penalty > best) {
        best = s - penalty
        bo = o
        bt = t
      }
    }
  }

  // Nothing found worth moving for: leave the line where the hand put it.
  if (best <= drawn + 0.004) return { a, b, score: drawn }

  return {
    a: { x: (ax + nx * (bo - bt)) / map.w, y: (ay + ny * (bo - bt)) / map.h },
    b: { x: (bx + nx * (bo + bt)) / map.w, y: (by + ny * (bo + bt)) / map.h },
    score: best,
  }
}

/**
 * Run the line out to where the roof edge actually stops.
 *
 * Nobody swipes the full length of a fascia. The finger starts late, lifts
 * early, and the run comes out two feet short at both ends - which on a quote
 * is real money, and on the picture is the thing that says "drawn by hand".
 *
 * The edge, though, keeps going, and we are already sitting on it. So walk
 * outward from each end along the line's own direction and keep going while the
 * photo underneath still shows an edge as strong as the part that WAS drawn.
 * When it fades - the gable ends, the fascia meets a wall - stop there.
 *
 * The reference is the drawn segment's own strength rather than a fixed
 * number, because a tile roof against bright sky and a dark fascia against
 * stucco produce completely different magnitudes and neither is wrong.
 */
function extendAlongEdge(
  map: EdgeMap,
  a: Point,
  b: Point,
): { a: Point; b: Point } {
  const ax = a.x * map.w
  const ay = a.y * map.h
  const bx = b.x * map.w
  const by = b.y * map.h
  const len = Math.hypot(bx - ax, by - ay)
  if (len < 8) return { a, b }
  const ux = (bx - ax) / len
  const uy = (by - ay) / len

  // How strong the edge is where the user actually drew. Anything we add has
  // to look like more of the same.
  const drawn = lineScore(map, ax, ay, bx, by)
  if (drawn < 0.05) return { a, b } // nothing convincing to follow

  const step = Math.max(3, len * 0.04)
  const floor = drawn * 0.55

  /** Walk from (sx,sy) in direction (dx,dy) while the edge holds. */
  const walk = (sx: number, sy: number, dx: number, dy: number) => {
    let out = 0
    let misses = 0
    /*
     * How far it may grow.
     *
     * This was 1.2x the drawn length, which assumes the user swiped most of the
     * run. They do not: a comfortable swipe covers maybe a third of a gable
     * rake, so the cap was stopping the run well short of the eave even when
     * the edge underneath carried on perfectly clearly. Three times, which
     * still refuses to run away across the whole photo.
     */
    const maxOut = len * 3
    for (let d = step; d <= maxOut; d += step) {
      const px = sx + dx * d
      const py = sy + dy * d
      if (px < 1 || py < 1 || px > map.w - 2 || py > map.h - 2) break
      /*
       * NOTHING IS EVER INSTALLED IN THE SKY.
       *
       * At a gable peak the tile silhouette against the sky is one of the
       * strongest gradients in the whole photograph, so following the edge
       * carried the run straight off the end of the roof and hung bulbs in
       * mid air above the peak. Which is exactly what the export showed.
       *
       * The edge test cannot catch this on its own - that IS a real edge. The
       * only thing that stops it is knowing which side of it is roof.
       */
      if (map.sky[(py | 0) * map.w + (px | 0)]) break
      const seg = lineScore(map, px - dx * step, py - dy * step, px, py)
      if (seg >= floor) {
        out = d
        misses = 0
      } else if (++misses >= 3) {
        // Three dead steps in a row is the edge ending. Fewer than that and a
        // vent, an aerial or a branch crossing the fascia stops the run early.
        break
      }
    }
    return out
  }

  const back = walk(ax, ay, -ux, -uy)
  const fwd = walk(bx, by, ux, uy)
  return {
    a: { x: (ax - ux * back) / map.w, y: (ay - uy * back) / map.h },
    b: { x: (bx + ux * fwd) / map.w, y: (by + uy * fwd) / map.h },
  }
}

/**
 * The straight line a set of points is scattered around, by least squares.
 *
 * Total least squares rather than the schoolbook y-on-x version, because a run
 * up a steep gable rake is nearly vertical and y-on-x blows up there. This
 * minimizes perpendicular distance, so it is equally happy with any slope.
 *
 * The returned ends are the extremes of the input projected onto that line, so
 * the fitted run covers the same stretch that was drawn.
 */
function fitLine(pts: Point[]): { a: Point; b: Point } | null {
  const n = pts.length
  if (n < 2) return null
  let mx = 0
  let my = 0
  for (const p of pts) {
    mx += p.x
    my += p.y
  }
  mx /= n
  my /= n

  let sxx = 0
  let syy = 0
  let sxy = 0
  for (const p of pts) {
    const dx = p.x - mx
    const dy = p.y - my
    sxx += dx * dx
    syy += dy * dy
    sxy += dx * dy
  }
  // Principal direction of the scatter.
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy)
  const ux = Math.cos(theta)
  const uy = Math.sin(theta)

  let tMin = Infinity
  let tMax = -Infinity
  for (const p of pts) {
    const t = (p.x - mx) * ux + (p.y - my) * uy
    if (t < tMin) tMin = t
    if (t > tMax) tMax = t
  }
  if (!isFinite(tMin) || tMax - tMin < 1e-6) return null

  return {
    a: { x: mx + ux * tMin, y: my + uy * tMin },
    b: { x: mx + ux * tMax, y: my + uy * tMax },
  }
}

/** Where two straight pieces would meet if both ran on. Null if near parallel. */
function intersect(a1: Point, a2: Point, b1: Point, b2: Point): Point | null {
  const d1x = a2.x - a1.x
  const d1y = a2.y - a1.y
  const d2x = b2.x - b1.x
  const d2y = b2.y - b1.y
  const den = d1x * d2y - d1y * d2x
  if (Math.abs(den) < 1e-9) return null
  const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / den
  // Only trust a corner close to where the two pieces already end. Two nearly
  // parallel roof planes meet somewhere off in the next county, and that is not
  // a corner anybody drew.
  if (t < -0.6 || t > 1.6) return null
  return { x: a1.x + d1x * t, y: a1.y + d1y * t }
}

/**
 * Turn any stroke - a two-tap line, a multi-tap outline, a freehand drag -
 * into clean straight pieces locked to the architecture.
 *
 * Pass the edge map as null to get the geometry cleanup (corners, straightness)
 * without the snapping, which is the right behavior before a photo has been
 * analyzed.
 */
/**
 * Is this stroke made of straight pieces, or is it a curve?
 *
 * It matters because the two want opposite treatment. A garage door outline is
 * corners, and forcing it straight is the whole point. An arch over a front
 * door is a curve, and forcing THAT straight turns it into a bent stick.
 *
 * A corner is a large turn at one point with near-nothing either side of it. A
 * curve is a small turn happening steadily all the way along. So: look at how
 * the total turning is distributed. Concentrated in a few places means corners;
 * spread evenly means a curve.
 */
function isCurve(pts: Point[]): boolean {
  if (pts.length < 8) return false

  /*
   * Judge the SHAPE, not the shake.
   *
   * The first version summed the absolute turn at every point, and a hand
   * drawing one straight line across a fascia racks up plenty of that purely
   * from wobble. So a perfectly ordinary roofline swipe was being called a
   * curve and smoothed instead of straightened, and came back exactly as bent
   * as it was drawn - which is the one thing this whole file exists to stop.
   *
   * Bulge is what actually separates the two. Measure how far the stroke bows
   * away from the straight line between its own two ends, as a fraction of that
   * line. A curve bows a long way. A shaky straight line does not bow at all,
   * however much it jitters, because the jitter cancels out.
   */
  const a = pts[0]
  const b = pts[pts.length - 1]
  const cx = b.x - a.x
  const cy = b.y - a.y
  const chord = Math.hypot(cx, cy)
  if (chord < 0.02) return false // a tiny scribble: nothing to straighten

  let bulge = 0
  let signed = 0
  for (const p of pts) {
    // Perpendicular distance from the chord, keeping the sign so that a shape
    // bowing consistently one way is told from one wandering either side.
    const d = ((p.x - a.x) * cy - (p.y - a.y) * cx) / chord
    if (Math.abs(d) > Math.abs(bulge)) bulge = d
    signed += d
  }
  const ratio = Math.abs(bulge) / chord
  if (ratio < 0.08) return false // straight enough to mean straight

  // A genuine arch bows one way throughout. A wandering hand averages out near
  // zero even when its worst excursion is large.
  const consistent = Math.abs(signed / pts.length) / Math.abs(bulge)
  if (consistent < 0.35) return false

  /*
   * A real corner settles it too. Nothing that curves smoothly turns this hard
   * in one step, so one sharp turn means a cornered shape - a garage door
   * outline - and those must be forced straight, bulge or no bulge.
   */
  for (let i = 1; i < pts.length - 1; i++) {
    const t0 = Math.atan2(pts[i].y - pts[i - 1].y, pts[i].x - pts[i - 1].x)
    const t1 = Math.atan2(pts[i + 1].y - pts[i].y, pts[i + 1].x - pts[i].x)
    let d = Math.abs(t1 - t0)
    if (d > Math.PI) d = Math.PI * 2 - d
    if (d > 0.9) return false
  }
  return true
}

/** Light smoothing that keeps the shape but loses the wobble of a real hand. */
function smoothCurve(pts: Point[], passes = 3): Point[] {
  let cur = pts
  for (let p = 0; p < passes; p++) {
    const out: Point[] = [cur[0]]
    for (let i = 1; i < cur.length - 1; i++) {
      out.push({
        x: cur[i - 1].x * 0.25 + cur[i].x * 0.5 + cur[i + 1].x * 0.25,
        y: cur[i - 1].y * 0.25 + cur[i].y * 0.5 + cur[i + 1].y * 0.25,
      })
    }
    out.push(cur[cur.length - 1])
    cur = out
  }
  // Thin it out; the renderer resamples anyway and a draggable run wants few
  // handles, not two hundred.
  return simplify(cur, 0.004)
}

/** Length in normalized units. The corners are normalized, so this is too. */
const segLen = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y)

export function straightenRun(
  pts: Point[],
  map: EdgeMap | null,
  /** Run the ends out to where the edge actually stops. Rooflines only. */
  extend = false,
): Point[] {
  if (pts.length < 2) return pts

  /*
   * TWO TAPS ARE TWO DECISIONS. NEITHER OF THEM MOVES.
   *
   * Growing the ends is right for a SWIPE, where the finger starts late and
   * lifts early and the ends are accidents. It is wrong for two taps: the
   * person aimed at a corner, pressed on it, and meant that. Reported as: I
   * drew the endpoint where that arrow is and it extended farther, I didn't
   * put it that far - the run carried on past the corner of the house and
   * hung bulbs along a line that is not a roof.
   *
   * A tap is a decision the tool does not get to overrule. Straightening still
   * happens: straightenSegment only slides and tilts PERPENDICULAR to the
   * line, so the run locks onto the fascia underneath without either end
   * traveling along it.
   */
  const chosenEnds = pts.length === 2
  if (chosenEnds) extend = false

  /*
   * AND IT KEEPS THE LENGTH IT WAS DRAWN AT.
   *
   * straightenSegment slides and TILTS: it moves the two ends perpendicular by
   * (o - t) and (o + t). The slide is harmless, but the tilt moves them in
   * opposite directions, and a segment whose ends move apart sideways is a
   * segment that got longer - by sqrt(len^2 + (2t)^2). Small, and plainly
   * visible on a short run against a corner you aimed at. Reported as: when I
   * click and extend it, it makes it longer or shorter.
   *
   * The tilt is worth keeping - it is what levels a run onto the fascia - so
   * the fix is to take the angle and refuse the length: scale the result back
   * about its own midpoint to exactly what the person drew.
   */
  const holdLength = (a: Point, b: Point, was: [Point, Point]): [Point, Point] => {
    const want = Math.hypot(was[1].x - was[0].x, was[1].y - was[0].y)
    const got = Math.hypot(b.x - a.x, b.y - a.y)
    if (want <= 0 || got <= 0) return [a, b]
    const k = want / got
    const mx = (a.x + b.x) / 2
    const my = (a.y + b.y) / 2
    return [
      { x: mx + (a.x - mx) * k, y: my + (a.y - my) * k },
      { x: mx + (b.x - mx) * k, y: my + (b.y - my) * k },
    ]
  }

  /*
   * ANY SHAPE, AND IT WORKS OUT WHAT YOU MEANT.
   *
   * An arch over a door and an outline round a garage are both "draw the shape
   * and it figures it out", but they are opposite problems: one must stay
   * curved, the other must be forced straight. Guessing wrong is very visible,
   * so the stroke itself decides - see isCurve.
   */
  if (isCurve(pts)) return smoothCurve(pts)

  /*
   * A ROOFLINE IS ONE STRAIGHT LINE. Make it one.
   *
   * A fascia, a rake, a ridge: each is a single straight edge, and any wander
   * in the result is error, not signal. RDP at a tolerance fine enough to keep
   * real corners also keeps every wobble of the hand and every wobble of the
   * edge tracer, which is how a plainly straight gable came back as eight
   * handles snaking above and below the actual roof.
   *
   * So when extending is on - rooflines only - fit ONE line through everything
   * by least squares and use that. A least-squares fit is not a smoothing of
   * the wander; it is the line the wander is scattered around, which is exactly
   * the roof edge. A genuine corner is a separate run, which is the same trade
   * two-click drawing already makes.
   */
  if (extend && pts.length > 2) {
    const fitted = fitLine(pts)
    if (fitted) {
      if (!map) return [fitted.a, fitted.b]
      const snapped = straightenSegment(map, fitted.a, fitted.b)

      /*
       * STRAIGHT UNLESS THE ROOF ITSELF IS NOT.
       *
       * Almost every roof edge is straight, and forcing it so is what stops a
       * gable coming back as a snake. But some genuinely are not - an arched
       * entry, a curved eave, a bowed porch - and flattening those is the same
       * mistake in the other direction.
       *
       * So ask the photo, not the hand. Score the straight fit against the
       * edge, and score the smoothed version of what was drawn against the same
       * edge. The hand's wander is random and will not sit on an edge; a real
       * curve will. Only when following the curve is CLEARLY better does the
       * curve win, because the straight answer is right far more often and a
       * tie should go to it.
       */
      const straightScore = lineScore(
        map,
        snapped.a.x * map.w,
        snapped.a.y * map.h,
        snapped.b.x * map.w,
        snapped.b.y * map.h,
      )
      const curve = smoothCurve(pts, 4)
      let curveScore = 0
      for (let i = 1; i < curve.length; i++) {
        curveScore += lineScore(
          map,
          curve[i - 1].x * map.w,
          curve[i - 1].y * map.h,
          curve[i].x * map.w,
          curve[i].y * map.h,
        )
      }
      curveScore /= Math.max(1, curve.length - 1)

      if (curveScore > straightScore * 1.35 && curve.length > 2) return curve

      // Straight it is: run both ends out to where that edge actually stops.
      const fwd = extendAlongEdge(map, snapped.a, snapped.b).b
      const back = extendAlongEdge(map, snapped.b, snapped.a).b
      return [back, fwd]
    }
  }

  // 1. Corners. A freehand drag is hundreds of points describing a handful of
  //    straight pieces; two taps are already just the two ends.
  const corners = pts.length > 2 ? simplify(pts, 0.006) : pts
  if (corners.length < 2) return pts
  if (!map) return corners

  // 2. Each piece onto its edge, independently.
  const legs: { a: Point; b: Point }[] = []
  for (let i = 1; i < corners.length; i++) {
    const r = straightenSegment(map, corners[i - 1], corners[i])
    if (chosenEnds) {
      const [a, b] = holdLength(r.a, r.b, [corners[i - 1], corners[i]])
      legs.push({ a, b })
    } else {
      legs.push({ a: r.a, b: r.b })
    }
  }

  /*
   * 2b. Run the OUTER ends out to where the edge stops.
   *
   * Only the two ends of the whole stroke, never an interior corner: a corner
   * is a place the user chose, and pushing it along would move the shape. The
   * ends are where the finger simply started late and lifted early.
   */
  if (extend) {
    const first = legs[0]
    first.a = extendAlongEdge(map, first.b, first.a).b
    const lastLeg = legs[legs.length - 1]
    lastLeg.b = extendAlongEdge(map, lastLeg.a, lastLeg.b).b
  }

  /*
   * 3. Put the corners back, WITHOUT letting one run away.
   *
   * Each leg has moved on its own, so consecutive legs no longer touch - a
   * garage door outline would come back as three disconnected sticks without
   * this, so the corner is where the two lines cross.
   *
   * The trap is that two lines which are nearly parallel cross a very long way
   * off. Independent straightening makes that common: a gentle bend in a
   * hand-drawn stroke is two legs a few degrees apart, and both get nudged
   * onto whatever is under them. The intersection then lands far outside the
   * stroke and the garland is laid all the way out to it and back - which is
   * the spike. Reported as: it is still making it too pointy, I think the
   * issue is when you turn, if I am making a curve around a garage that is
   * where it becomes pointy.
   *
   * So the corner is only accepted if it is near the corner it replaces. A
   * miter limit, in other words, and the fallback is the same one used when
   * the lines are exactly parallel: meet in the middle. That rounds a bend
   * instead of spiking it, which is what garland does anyway.
   */
  const out: Point[] = [legs[0].a]
  for (let i = 1; i < legs.length; i++) {
    const prev = legs[i - 1]
    const cur = legs[i]
    const meet = { x: (prev.b.x + cur.a.x) / 2, y: (prev.b.y + cur.a.y) / 2 }
    const c = intersect(prev.a, prev.b, cur.a, cur.b)
    if (!c) {
      out.push(meet)
      continue
    }
    // How far the crossing may sit from the corner it stands in for: half the
    // shorter leg, with a floor so a short leg still gets a little room.
    const near = corners[i]
    const lim = Math.max(
      0.01,
      Math.min(segLen(prev.a, prev.b), segLen(cur.a, cur.b)) * 0.5,
    )
    out.push(Math.hypot(c.x - near.x, c.y - near.y) <= lim ? c : meet)
  }
  out.push(legs[legs.length - 1].b)

  return out.map((p) => ({ x: Math.min(1, Math.max(0, p.x)), y: Math.min(1, Math.max(0, p.y)) }))
}
